import { OVERRIDE_WORDS, titleCase, type CardChange, type CarryBackResultDto, type CarryToldDto, type ProcessDto, type ProcessStepDto, type StepMasterDto, type TaskOwner } from '@nsb/core';
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { dateWords } from '../../days.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { Button, ErrorLine, TextInput, Toggle } from '../ui.tsx';
import { useUnsaved } from '../useUnsaved.ts';
import { TradeOptions } from './TradeOptions.tsx';
import { cardChange, cardChangeNames, cardItems, cardOf, clientLines, followsLine, givesBack, isOwn, masterButton, ownerLabel, warningOf, type CardForm, type Warning } from './carryBack.ts';
import { processAction, useTrades, type ProcessModel } from './useProcess.ts';

/**
 * A project's step card (design study 25, "Carry a Change Back", as Jason answered it on 2026-10-08): on the setup
 * page, a step that came from the master opens up to the master card's fields (its title, what it is, what the client
 * reads, who does it, its trade and its checklist) and its five switches; each is the master's until changed on this
 * project, and says so. Two buttons: Save for this project (held in the job's draft, as every change on this page),
 * and Save, and update the master tree (an admin's, refused while the master's own changes wait), which first warns
 * which projects will follow the change and which will not, and names whose clients will be told a step now waits on
 * them (Jason, 2026-10-08, "Let it notify, name it"). Back to the master's gives the whole step back.
 */

const OWNER_WORDS: Record<TaskOwner, string> = { nsb: 'NSB', sub: 'A sub', vendor: 'A supplier', client: 'The client', authority: 'Outside', arch: 'The engineer' };

/** Who can do it, by its kind, as the master's card offers it: a gate nobody approves happens. */
function ownersFor(kind: ProcessStepDto['kind'], current: TaskOwner | null): (TaskOwner | null)[] {
  const base: (TaskOwner | null)[] =
    kind === 'gate' ? ['client', 'sub', 'nsb', null] : kind === 'decision' ? ['client', 'nsb'] : kind === 'milestone' ? [] : ['nsb', 'sub', 'vendor', 'client', 'authority'];
  return current !== null && !base.includes(current) && kind !== 'milestone' ? [...base, current] : base;
}

/** The master's, or this project's own: a word beside a field, never colour alone. */
function Mark({ own }: { own: boolean }) {
  return <span className={`meta ${own ? '!text-cedar' : ''}`}>{own ? 'This project’s own' : 'The master’s'}</span>;
}

function Row({ label, own, children }: { label: string; own: boolean | null; children: ReactNode }) {
  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="label">{label}</span>
        {own === null ? null : <Mark own={own} />}
      </div>
      {children}
    </div>
  );
}

export function StepCard({ projectId, model, step: s }: { projectId: string; model: ProcessModel; step: ProcessStepDto }) {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { data: trades } = useTrades();
  const { data: master } = useApi<StepMasterDto>(['project', projectId, 'process', 'master', s.id], `/projects/${projectId}/process/steps/${s.id}/master`);
  const was = cardOf(model, s);
  const items = cardItems(model, s);
  const [form, setForm] = useState<CardForm>(was);
  // The card as it was when the form was last filled from it: a save, or the job changing under it, fills it again.
  const [from, setFrom] = useState(JSON.stringify(was));
  if (from !== JSON.stringify(was)) {
    setFrom(JSON.stringify(was));
    setForm(was);
  }
  const [item, setItem] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warning, setWarning] = useState(false);
  const [givingBack, setGivingBack] = useState(false);
  const [carried, setCarried] = useState<CarryBackResultDto | null>(null);
  // Whose clients the change would tell, read when the warning opens (the carry-back made and rolled back).
  const [told, setTold] = useState<CarryToldDto | null>(null);
  const change: CardChange = cardChange(was, form);
  const changed = Object.keys(change).length > 0;
  const m = master?.master ?? null;
  const own = (f: Parameters<typeof isOwn>[0]) => (m ? isOwn(f, form, m) : null);
  const button = masterButton(admin, master);
  const set = (patch: Partial<CardForm>) => {
    setCarried(null);
    // The warning read the change as it was: changed, it is asked again.
    setWarning(false);
    setForm((f) => ({ ...f, ...patch }));
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const save = () => act(() => processAction(projectId, `/steps/${s.id}/card`, change));
  // The card's changes not saved for this project yet, on the bar at the window's foot, its button this one (P-29).
  useUnsaved({
    names: cardChangeNames(change, s.kind),
    saveWords: 'Save for this project',
    busy,
    error: err,
    onSave: save,
    onDiscard: () => {
      setForm(was);
      setItem('');
      setWarning(false);
      setErr(null);
    },
  });
  const giveBack = () => act(() => processAction(projectId, `/steps/${s.id}/back`)).then((ok) => ok && setGivingBack(false));
  /** The warning, and whose clients the change would tell (the API makes the carry-back and rolls it back). */
  const asked = useRef(0);
  const openWarning = () => {
    const ask = ++asked.current;
    setErr(null);
    setTold(null);
    setWarning(true);
    // Only the latest ask is read: one sent for a change since made over is passed over.
    void api<CarryToldDto>(`/projects/${projectId}/process/steps/${s.id}/carry-back/told`, { body: change, notice: false }).then(
      (t) => ask === asked.current && setTold(t),
      (e: unknown) => ask === asked.current && setErr(errorText(e)),
    );
  };
  const carry = () =>
    act(async () => {
      // Made at once, whatever page asks (q-when A): the job's other changes go on waiting in its draft.
      const dto = await api<ProcessDto & { carried: CarryBackResultDto }>(`/projects/${projectId}/process/steps/${s.id}/carry-back?draft=1`, { body: change, notice: 'Saved, and the master tree updated' });
      setCarried(dto.carried);
      setWarning(false);
      // Everything that reads the job, or the master: this job's pages, and the master's page and its lock-in.
      void queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] === 'project' && q.queryKey[1] === projectId) || q.queryKey[0] === 'builder' });
    });

  const kind = s.kind;
  const owners = ownersFor(kind, form.owner);
  const fields = Object.keys(was.switches) as (keyof typeof was.switches)[];
  const removed = new Set(form.off);
  const live = trades?.trades ?? [];
  const warn = master ? warningOf(master, form, told) : null;

  return (
    <div className="border-t border-rule py-3">
      <div className="label mb-1.5">On this job</div>
      <p className="meta">{followsLine(s, (iso) => dateWords(iso))}</p>

      <Row label="Title" own={own('title')}>
        <TextInput aria-label="Title" value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} />
      </Row>
      <Row label="For NSB" own={own('why')}>
        <textarea className="field resize-y text-sm" rows={2} aria-label="For NSB" value={form.why} maxLength={4000} onChange={(e) => set({ why: e.target.value })} placeholder="In a sentence or two" />
      </Row>
      {kind === 'milestone' ? null : (
        <Row label="For the client" own={own('clientNote')}>
          <textarea className="field resize-y text-sm" rows={2} aria-label="For the client" value={form.clientNote} maxLength={2000} onChange={(e) => set({ clientNote: e.target.value })} placeholder="Blank: they see its tasks only" />
        </Row>
      )}
      {owners.length ? (
        <Row label={ownerLabel(kind)} own={own('owner')}>
          <select className="field max-w-60" aria-label={ownerLabel(kind)} value={form.owner ?? ''} onChange={(e) => set({ owner: (e.target.value || null) as TaskOwner | null })}>
            {owners.map((o) => (
              <option key={o ?? 'none'} value={o ?? ''}>
                {o ? OWNER_WORDS[o] : 'Nobody: it happens'}
              </option>
            ))}
          </select>
        </Row>
      ) : null}
      {kind === 'work' ? (
        <Row label="Its trade" own={own('trade')}>
          <select className="field max-w-80" aria-label="Its trade" value={form.trade ?? ''} onChange={(e) => set({ trade: e.target.value || null })}>
            <option value="">Not a package</option>
            <TradeOptions trades={live} current={form.trade} />
          </select>
        </Row>
      ) : null}

      <Row label={`Checklist · ${items.filter((i) => !removed.has(i.id)).length + form.add.length}`} own={null}>
        <ul className="border-t border-rule">
          {items.map((i) => (
            <li key={i.id} className="flex min-h-9 items-center justify-between gap-3 border-b border-rule py-1 text-sm">
              <span className={removed.has(i.id) ? 'text-ink-3 line-through' : ''}>{titleCase(i.title)}</span>
              {i.hold ? (
                <span className="meta">set by its switch</span>
              ) : i.done ? (
                <span className="meta">done</span>
              ) : (
                <button type="button" className="link min-h-9 text-ink-3 hover:text-ink" onClick={() => set({ off: removed.has(i.id) ? form.off.filter((x) => x !== i.id) : [...form.off, i.id] })}>
                  {removed.has(i.id) ? 'Keep it' : 'Take out'}
                </button>
              )}
            </li>
          ))}
          {form.add.map((t, n) => (
            <li key={`add-${n}`} className="flex min-h-9 items-center justify-between gap-3 border-b border-rule py-1 text-sm">
              <span>{titleCase(t)}</span>
              <button type="button" className="link min-h-9 text-ink-3 hover:text-ink" onClick={() => set({ add: form.add.filter((_x, k) => k !== n) })}>
                Take out
              </button>
            </li>
          ))}
        </ul>
        <form
          className="mt-1 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!item.trim()) return;
            set({ add: [...form.add, item.trim()] });
            setItem('');
          }}
        >
          <input className="field min-w-0 flex-1" value={item} onChange={(e) => setItem(e.target.value)} placeholder="A new item…" aria-label="A new item on its checklist" maxLength={200} />
          <button type="submit" className="link min-h-11 text-sm" disabled={!item.trim()}>
            Add
          </button>
        </form>
      </Row>

      {fields.length ? (
        <div className="mt-3">
          {fields.map((f) => (
            <div key={f} className="flex flex-wrap items-center justify-between gap-x-4">
              <Toggle checked={Boolean(form.switches[f])} label={`${OVERRIDE_WORDS[f].charAt(0).toUpperCase()}${OVERRIDE_WORDS[f].slice(1)}`} onChange={(v) => set({ switches: { ...form.switches, [f]: v } })} />
              {own(f) === null ? null : <Mark own={own(f)!} />}
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button tone="secondary" busy={busy} disabled={!changed} onClick={() => void save()}>
          Save for this project
        </Button>
        {/* The hover on a button greyed out is its wrapper's: a disabled button takes no pointer. */}
        <span title={button.why ?? undefined}>
          <Button tone="secondary" busy={busy} disabled={button.disabled || (!changed && s.follows?.follows !== false)} onClick={openWarning}>
            Save, and update the master tree
          </Button>
        </span>
      </div>
      {button.why && admin && master?.pending ? (
        <p className="meta mt-1">
          {master.pending}{' '}
          <Link to="/settings/process" className="link">
            Open the master process tree
          </Link>
        </p>
      ) : button.why && !admin ? (
        <p className="meta mt-1">{button.why}</p>
      ) : null}

      {warning && warn ? <CarryWarning warn={warn} step={form.title || s.title} busy={busy} looking={!told && !err} onConfirm={() => void carry()} onCancel={() => setWarning(false)} /> : null}
      {carried ? (
        <p className="mt-2 text-sm">
          The master tree is version {carried.version} now{carried.followed ? `, and ${carried.followed} other project${carried.followed === 1 ? '' : 's'} followed it` : ''}.
        </p>
      ) : null}

      {givesBack(s) ? (
        givingBack ? (
          <div className="mt-3 text-sm">
            <p>Give every field, switch, item and wait back to the master’s? The step follows the master again.</p>
            <div className="mt-1 flex flex-wrap gap-x-4">
              <Button tone="danger" busy={busy} onClick={() => void giveBack()}>
                Give it back
              </Button>
              <Button tone="quiet" onClick={() => setGivingBack(false)}>
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <button type="button" className="link mt-3 min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => setGivingBack(true)}>
            Back to the master’s
          </button>
        )
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}

/**
 * The warning before the master changes (q-warn B; Jason, 2026-10-08, "Let it notify, name it"): what the master is and
 * who follows it, the other projects in groups, test projects apart, whose clients read new words, and whose clients
 * will be told a step now waits on them (`clientLines`), then Confirm or Cancel. `looking` while whose clients are told
 * is still being read: Confirm waits, and says so, until they are named or the look fails, so nobody is told who was
 * not named first.
 */
export function CarryWarning({ warn, step, busy, looking, onConfirm, onCancel }: { warn: Warning; step: string; busy: boolean; looking: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="mt-3 border-y border-rule-strong py-3" role="group" aria-label="Before the master tree changes">
      <p className="text-sm">
        This updates the master process tree, which every project inherits. Only a step nobody has changed on its project follows it: where anything on it was changed and saved, that project keeps its own and does not get this.
      </p>
      {warn.groups.length ? (
        warn.groups.map((g) => (
          <div key={g.group} className="mt-2">
            <div className="label">
              {g.title} · {g.names.length}
            </div>
            <p className="text-sm">{g.names.join(', ')}</p>
            <p className="meta">{g.line}</p>
          </div>
        ))
      ) : (
        <p className="meta mt-2">No other project has this step.</p>
      )}
      {warn.tests.length ? (
        <div className="mt-2">
          <div className="label">Test projects · {warn.tests.length}</div>
          <p className="text-sm">{warn.tests.map((t) => `${t.name} (${t.what})`).join(', ')}</p>
        </div>
      ) : null}
      {clientLines(warn, step).map((line) => (
        <p key={line} className="mt-2 text-sm">
          {line}
        </p>
      ))}
      {looking ? <p className="meta mt-2">Finding whose clients this would tell… Confirm waits until it knows.</p> : null}
      <p className="meta mt-2">Completed and archived jobs keep theirs as they ended. This project follows the master again on what it carries back.</p>
      <div className="mt-2 flex flex-wrap gap-x-4">
        <Button busy={busy} disabled={looking} onClick={onConfirm}>
          Confirm
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
